import { useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';

import TitleBar from '#components/TitleBar';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import {
  useGetCouponMutation,
  useSaveCouponMutation,
} from '#services/coupon';
import { useGetCollectionsForUserMutation } from '#services/collection';
import { useGetProductsQuery } from '#services/product';
import { useGetAllCheckoutAsUsersQuery } from '#services/user';
import URLSearchParamsEx from '#utils/url-ex';
import {
  COUPON_STATUS,
  COUPON_DISCOUNT_TYPES,
  COUPON_TARGET_TYPES,
  COUPON_ASSIGNMENT_TYPES,
} from '#common/constants';

interface CouponFormValues {
  id?: number | null;
  code?: string;
  name?: string;
  description?: string;
  discountType?: string;
  discountValue?: string | number;
  targetType?: string;
  targetIds?: Array<number | string>;
  minPurchaseAmount?: string | number;
  startDate?: string;
  endDate?: string;
  assignmentType?: string;
  assignedCustomerIds?: Array<number | string>;
  multipleUse?: boolean;
  status?: string;
  [key: string]: unknown;
}

interface OptionItem {
  id?: number | string | undefined;
  name?: string | undefined;
  [key: string]: unknown;
}

interface CustomerItem {
  id?: number | string;
  username?: string;
  role?: string;
  [key: string]: unknown;
}

// A permissive shape covering the various MUI change events (TextField, Select,
// Switch) handleChange is wired to.
type ChangeLike = {
  target: { name?: string; value?: unknown; type?: string; checked?: boolean };
};

const getInitialState = (): CouponFormValues => ({
  id: null,
  code: '',
  name: '',
  description: '',
  discountType: COUPON_DISCOUNT_TYPES.PERCENT,
  discountValue: '',
  targetType: COUPON_TARGET_TYPES.CART,
  targetIds: [],
  minPurchaseAmount: '',
  startDate: new Date().toISOString().split('T')[0]!,
  endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]!,
  assignmentType: COUPON_ASSIGNMENT_TYPES.ALL,
  assignedCustomerIds: [],
  multipleUse: false,
  status: COUPON_STATUS.ACTIVE,
});

export default function CouponEdit() {
  const {
    showBackdrop,
    hideBackdrop,
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = new URLSearchParamsEx(location as unknown as Record<string, unknown>);
  const couponId = urlParams.values.id ? Number(urlParams.values.id) : null;
  const isEdit = !!couponId;

  const [values, setValues] = useState<CouponFormValues>(getInitialState());
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [collections, setCollections] = useState<OptionItem[]>([]);
  const [products, setProducts] = useState<OptionItem[]>([]);
  const [customers, setCustomers] = useState<CustomerItem[]>([]);
  const [selectedTargets, setSelectedTargets] = useState<Array<number | string>>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<Array<number | string>>([]);

  const [getCoupon] = useGetCouponMutation();
  const [saveCoupon] = useSaveCouponMutation();
  const [getCollections] = useGetCollectionsForUserMutation();

  // Fetch products with large page size to get all
  const productsQuery = useGetProductsQuery({
    pageIndex: 0,
    pageSize: 9999,
    all: true,
  });

  // Fetch all checkout-as users (includes customers)
  const customersQuery = useGetAllCheckoutAsUsersQuery(undefined);

  // Load collections on mount
  useEffect(() => {
    void getCollections({ all: true }).then((res) => {
      const data = ('data' in res ? res.data : undefined);
      if (data) {
        // Handle both formats: res.data could be array directly or { error, data } object
        const collectionsData = Array.isArray(data)
          ? data
          : ((data as { data?: OptionItem[] }).data || []);
        setCollections(collectionsData as OptionItem[]);
      }
    });
  }, []);

  // Set products when query completes
  useEffect(() => {
    const data = productsQuery.data as { data?: { rows?: OptionItem[] }; rows?: OptionItem[] } | undefined;
    if (data) {
      // Products API returns { error, data: { rows } }
      const productsData = data.data?.rows
        || data.rows
        || [];
      setProducts(productsData);
    }
  }, [productsQuery.data]);

  // Set customers when query completes
  useEffect(() => {
    const data = customersQuery.data as CustomerItem[] | { data?: CustomerItem[] } | undefined;
    if (data) {
      // Handle both formats: data could be array directly or { error, data } object
      const rawCustomers = Array.isArray(data)
        ? data
        : (data.data || []);
      // Filter to only include customers
      const customerUsers = rawCustomers.filter((u) => u.role === 'customer');
      setCustomers(customerUsers);
    }
  }, [customersQuery.data]);

  // Load coupon data if editing
  useEffect(() => {
    if (!couponId) return;

    showBackdrop();
    getCoupon({ id: couponId })
      .then((res) => {
        const data = ('data' in res ? res.data : undefined) as { error?: unknown; data?: CouponFormValues } & CouponFormValues | undefined;
        if (data && !data.error) {
          // Handle both possible response structures
          const coupon = (data.data || data);

          setValues({
            ...coupon,
            startDate: new Date(coupon.startDate as string).toISOString().split('T')[0]!,
            endDate: new Date(coupon.endDate as string).toISOString().split('T')[0]!,
            minPurchaseAmount: coupon.minPurchaseAmount || '',
          });
          setSelectedTargets(coupon.targetIds || []);
          setSelectedCustomers(coupon.assignedCustomerIds || []);
        } else {
          simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0]);
        }
      })
      .catch((_err) => {
        simpleSnackbar({ data: { error: true, message: 'Failed to load coupon data' } } as unknown as Parameters<typeof simpleSnackbar>[0]);
      })
      .finally(() => {
        hideBackdrop();
      });
  }, [couponId]);

  const handleChange = (e: ChangeLike) => {
    const { name, value, type, checked } = e.target;
    setValues((prev) => ({
      ...prev,
      [name as string]: type === 'checkbox' ? checked : value,
    }));

    // Clear error when field is changed
    if (name && errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!values.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!values.discountValue || Number(values.discountValue) <= 0) {
      newErrors.discountValue = 'Discount value must be greater than 0';
    }

    if (values.discountType === COUPON_DISCOUNT_TYPES.PERCENT
        && Number(values.discountValue) > 100) {
      newErrors.discountValue = 'Percentage cannot exceed 100';
    }

    if (values.minPurchaseAmount && Number(values.minPurchaseAmount) < 0) {
      newErrors.minPurchaseAmount = 'Minimum purchase must be 0 or greater';
    }

    if (values.targetType !== COUPON_TARGET_TYPES.CART && selectedTargets.length === 0) {
      newErrors.targetIds = 'At least one target must be selected';
    }

    if (!values.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!values.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (values.startDate && values.endDate
        && new Date(values.startDate) > new Date(values.endDate)) {
      newErrors.endDate = 'End date must be on or after start date';
    }

    if (values.assignmentType === COUPON_ASSIGNMENT_TYPES.SPECIFIC
        && selectedCustomers.length === 0) {
      newErrors.assignedCustomerIds = 'At least one customer must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    showBackdrop();
    const postData = {
      ...values,
      discountValue: Number(values.discountValue),
      minPurchaseAmount: values.minPurchaseAmount ? Number(values.minPurchaseAmount) : null,
      targetIds: values.targetType === COUPON_TARGET_TYPES.CART ? [] : selectedTargets,
      assignedCustomerIds: values.assignmentType === COUPON_ASSIGNMENT_TYPES.ALL
        ? []
        : selectedCustomers,
    };

    void saveCoupon(postData)
      .then((res) => {
        const action = isEdit ? 'updated' : 'created';
        const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], `Coupon was successfully ${action}.`);

        if (!hasError) {
          void navigate('/management/coupon/list');
        }
      })
      .finally(() => {
        hideBackdrop();
      });
  };

  // Get options for target selection based on target type
  const getTargetOptions = (): OptionItem[] => {
    if (values.targetType === COUPON_TARGET_TYPES.COLLECTION) {
      return collections.map((c) => ({ id: c.id, name: c.name }));
    }
    if (values.targetType === COUPON_TARGET_TYPES.PRODUCT) {
      return products.map((p) => ({ id: p.id, name: p.name }));
    }
    return [];
  };

  const getSelectedTargetObjects = (): OptionItem[] => {
    const options = getTargetOptions();
    return selectedTargets.map((id) => options.find((o) => o.id === id)).filter(Boolean) as OptionItem[];
  };

  const getSelectedCustomerObjects = (): CustomerItem[] => selectedCustomers
    .map((id) => customers.find((c) => c.id === id))
    .filter(Boolean) as CustomerItem[];

  return (
    <>
      <TitleBar title={`${isEdit ? 'Edit' : 'Create'} Coupon`} />
      {isEdit && values.code && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Coupon Code:
          {' '}
          <strong style={{ fontFamily: 'monospace' }}>{values.code}</strong>
        </Alert>
      )}
      <Grid container spacing={3}>
        {/* Basic Info */}
        <Grid size={12}>
          <Typography variant="subtitle1" gutterBottom sx={{
            fontWeight: "bold"
          }}>
            Basic Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            required
            fullWidth
            size="small"
            name="name"
            label="Coupon Name"
            value={values.name}
            error={!!errors.name}
            helperText={errors.name || 'Used to generate the coupon code'}
            onChange={handleChange}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <FormControlLabel
            control={(
              <Switch
                name="status"
                checked={values.status === COUPON_STATUS.ACTIVE}
                onChange={(e) => {
                  setValues((prev) => ({
                    ...prev,
                    status: e.target.checked ? COUPON_STATUS.ACTIVE : COUPON_STATUS.DISABLED,
                  }));
                }}
              />
            )}
            label="Active"
          />
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            size="small"
            name="description"
            label="Description (Optional)"
            value={values.description}
            onChange={handleChange}
            multiline
            rows={2}
            helperText="Customer-facing description"
          />
        </Grid>

        {/* Discount Configuration */}
        <Grid size={12}>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              fontWeight: "bold",
              mt: 2
            }}>
            Discount Configuration
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControl fullWidth size="small">
            <InputLabel>Discount Type</InputLabel>
            <Select
              name="discountType"
              value={values.discountType}
              label="Discount Type"
              onChange={handleChange}
            >
              <MenuItem value={COUPON_DISCOUNT_TYPES.PERCENT}>Percentage (%)</MenuItem>
              <MenuItem value={COUPON_DISCOUNT_TYPES.FIXED}>Fixed Amount ($)</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            required
            fullWidth
            size="small"
            name="discountValue"
            label="Discount Value"
            type="number"
            value={values.discountValue}
            error={!!errors.discountValue}
            helperText={errors.discountValue}
            onChange={handleChange}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    {values.discountType === COUPON_DISCOUNT_TYPES.PERCENT ? '%' : '$'}
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            size="small"
            name="minPurchaseAmount"
            label="Minimum Purchase (Optional)"
            type="number"
            value={values.minPurchaseAmount}
            onChange={handleChange}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              },
            }}
            helperText="Minimum cart total required"
          />
        </Grid>

        {/* Targeting */}
        <Grid size={12}>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              fontWeight: "bold",
              mt: 2
            }}>
            Targeting
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControl fullWidth size="small">
            <InputLabel>Target Type</InputLabel>
            <Select
              name="targetType"
              value={values.targetType}
              label="Target Type"
              onChange={(e) => {
                handleChange(e);
                setSelectedTargets([]);
              }}
            >
              <MenuItem value={COUPON_TARGET_TYPES.CART}>Entire Cart</MenuItem>
              <MenuItem value={COUPON_TARGET_TYPES.COLLECTION}>Specific Collections</MenuItem>
              <MenuItem value={COUPON_TARGET_TYPES.PRODUCT}>Specific Products</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {values.targetType !== COUPON_TARGET_TYPES.CART && (
          <Grid
            size={{
              xs: 12,
              md: 8
            }}>
            <Autocomplete
              multiple
              size="small"
              options={getTargetOptions()}
              getOptionLabel={(option) => option.name || ''}
              value={getSelectedTargetObjects()}
              onChange={(e, newValue) => {
                setSelectedTargets(newValue.map((v) => v.id as number | string));
                if (errors.targetIds) {
                  setErrors((prev) => ({ ...prev, targetIds: null }));
                }
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={values.targetType === COUPON_TARGET_TYPES.COLLECTION
                    ? 'Select Collections'
                    : 'Select Products'}
                  error={!!errors.targetIds}
                  helperText={errors.targetIds}
                />
              )}
              renderValue={(value, getItemProps) => value.map((option, index) => {
                const { key, ...tagProps } = getItemProps({ index });
                return (
                  <Chip
                    key={option.id ?? key}
                    size="small"
                    label={option.name}
                    {...tagProps}
                  />
                );
              })}
            />
          </Grid>
        )}

        {/* Validity Period */}
        <Grid size={12}>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              fontWeight: "bold",
              mt: 2
            }}>
            Validity Period
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            required
            fullWidth
            size="small"
            name="startDate"
            label="Start Date"
            type="date"
            value={values.startDate}
            error={!!errors.startDate}
            helperText={errors.startDate}
            onChange={handleChange}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            required
            fullWidth
            size="small"
            name="endDate"
            label="End Date"
            type="date"
            value={values.endDate}
            error={!!errors.endDate}
            helperText={errors.endDate}
            onChange={handleChange}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Grid>

        {/* Customer Assignment */}
        <Grid size={12}>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              fontWeight: "bold",
              mt: 2
            }}>
            Customer Assignment
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControl fullWidth size="small">
            <InputLabel>Assignment</InputLabel>
            <Select
              name="assignmentType"
              value={values.assignmentType}
              label="Assignment"
              onChange={(e) => {
                handleChange(e);
                if (e.target.value === COUPON_ASSIGNMENT_TYPES.ALL) {
                  setSelectedCustomers([]);
                }
              }}
            >
              <MenuItem value={COUPON_ASSIGNMENT_TYPES.ALL}>All Customers</MenuItem>
              <MenuItem value={COUPON_ASSIGNMENT_TYPES.SPECIFIC}>Specific Customers</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {values.assignmentType === COUPON_ASSIGNMENT_TYPES.SPECIFIC && (
          <Grid
            size={{
              xs: 12,
              md: 8
            }}>
            <Autocomplete
              multiple
              size="small"
              options={customers}
              getOptionLabel={(option) => option.username || `Customer #${option.id}`}
              value={getSelectedCustomerObjects()}
              onChange={(e, newValue) => {
                setSelectedCustomers(newValue.map((v) => v.id as number | string));
                if (errors.assignedCustomerIds) {
                  setErrors((prev) => ({ ...prev, assignedCustomerIds: null }));
                }
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Customers"
                  error={!!errors.assignedCustomerIds}
                  helperText={errors.assignedCustomerIds}
                />
              )}
              renderValue={(value, getItemProps) => value.map((option, index) => {
                const { key, ...tagProps } = getItemProps({ index });
                return (
                  <Chip
                    key={option.id ?? key}
                    size="small"
                    label={option.username}
                    {...tagProps}
                  />
                );
              })}
            />
          </Grid>
        )}

        {/* Usage Rules */}
        <Grid size={12}>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              fontWeight: "bold",
              mt: 2
            }}>
            Usage Rules
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid size={12}>
          <FormControlLabel
            control={(
              <Switch
                name="multipleUse"
                checked={values.multipleUse}
                onChange={handleChange}
              />
            )}
            label="Allow multiple uses per customer"
          />
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block"
            }}>
            {values.multipleUse
              ? 'Customers can use this coupon on multiple orders'
              : 'Each customer can only use this coupon once'}
          </Typography>
        </Grid>

        {/* Actions */}
        <Grid size={12}>
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
            >
              {isEdit ? 'Save Changes' : 'Create Coupon'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => { void navigate('/management/coupon/list'); }}
            >
              Cancel
            </Button>
          </Box>
        </Grid>
      </Grid>
    </>
  );
}
